import type { DocCategory } from '../registry';
import HowItWorks, { toc as howItWorksToc } from '../pages/concepts/how-it-works';
import AnalyzingMedia, { toc as analyzingMediaToc } from '../pages/concepts/analyzing-media';
import VisualSearch, { toc as visualSearchToc } from '../pages/concepts/visual-search';
import SpeechSearch, { toc as speechSearchToc } from '../pages/concepts/speech-search';
import PeopleAndFaces, { toc as peopleAndFacesToc } from '../pages/concepts/people-and-faces';
import UltraSearch, { toc as ultraSearchToc } from '../pages/concepts/ultra-search';
import ModelsAndYourGpu, { toc as modelsAndYourGpuToc } from '../pages/concepts/models-and-your-gpu';
import LocalAndPrivate, { toc as localAndPrivateToc } from '../pages/concepts/local-and-private';
import AgenticEditing, { toc as agenticEditingToc } from '../pages/concepts/agentic-editing';
import Licensing, { toc as licensingToc } from '../pages/concepts/licensing';

export const concepts: DocCategory = {
  id: 'concepts',
  label: 'Core concepts',
  pages: [
    {
      slug: 'concepts/how-it-works',
      title: 'How ClipRadar works',
      eyebrow: 'Core concepts',
      description: 'Analyze once on your GPU, then search instantly and locally.',
      toc: howItWorksToc,
      Body: HowItWorks,
      related: [
        { label: 'Analyzing your footage', to: '/docs/concepts/analyzing-media' },
        { label: 'Local & private', to: '/docs/concepts/local-and-private' },
        { label: 'Quickstart', to: '/docs/quickstart' },
      ],
    },
    {
      slug: 'concepts/analyzing-media',
      title: 'Analyzing your footage',
      eyebrow: 'Core concepts',
      description: 'Analyze before you search — what each pass does, and why moving or renaming files is safe.',
      toc: analyzingMediaToc,
      Body: AnalyzingMedia,
      related: [
        { label: 'How ClipRadar works', to: '/docs/concepts/how-it-works' },
        { label: 'Media tab', to: '/docs/interface/media' },
        { label: 'AI models & your GPU', to: '/docs/concepts/models-and-your-gpu' },
        { label: 'Where ClipRadar stores things', to: '/docs/reference/where-your-data-lives' },
      ],
    },
    {
      slug: 'concepts/visual-search',
      title: 'Visual search',
      eyebrow: 'Core concepts',
      description: 'Describe a shot in plain language and ClipRadar reads the pixels to find it.',
      toc: visualSearchToc,
      Body: VisualSearch,
      related: [
        { label: 'Get better search results', to: '/docs/guides/search-best-practices' },
        { label: 'Ultra Search', to: '/docs/concepts/ultra-search' },
        { label: 'Search tab', to: '/docs/interface/search' },
      ],
    },
    {
      slug: 'concepts/speech-search',
      title: 'Speech search',
      eyebrow: 'Core concepts',
      description: 'Find the exact words anyone said, jump to the timecode, and pull the line into your edit.',
      toc: speechSearchToc,
      Body: SpeechSearch,
      related: [
        { label: 'Fix speech & transcript problems', to: '/docs/guides/fix-speech-problems' },
        { label: 'Search tab', to: '/docs/interface/search' },
        { label: 'Machine-learning models', to: '/docs/reference/models' },
      ],
    },
    {
      slug: 'concepts/people-and-faces',
      title: 'People & faces',
      eyebrow: 'Core concepts',
      description: 'Name a face once, then find that person in every clip — plus the honest story on splits.',
      toc: peopleAndFacesToc,
      Body: PeopleAndFaces,
      related: [
        { label: 'Find & name people', to: '/docs/guides/find-and-name-people' },
        { label: 'People tab', to: '/docs/interface/people' },
        { label: 'How ClipRadar works', to: '/docs/concepts/how-it-works' },
      ],
    },
    {
      slug: 'concepts/ultra-search',
      title: 'Ultra Search',
      eyebrow: 'Core concepts',
      description: 'One sentence, every search signal at once — parsed locally into editable chips.',
      toc: ultraSearchToc,
      Body: UltraSearch,
      related: [
        { label: 'AI models & your GPU', to: '/docs/concepts/models-and-your-gpu' },
        { label: 'Get better search results', to: '/docs/guides/search-best-practices' },
        { label: 'Search tab', to: '/docs/interface/search' },
      ],
    },
    {
      slug: 'concepts/models-and-your-gpu',
      title: 'AI models & your GPU',
      eyebrow: 'Core concepts',
      description: 'Your GPU is where ClipRadar does its work — what VRAM buys you, and how it adapts.',
      toc: modelsAndYourGpuToc,
      Body: ModelsAndYourGpu,
      related: [
        { label: 'System requirements', to: '/docs/reference/system-requirements' },
        { label: 'Machine-learning models', to: '/docs/reference/models' },
        { label: "ClipRadar can't find my GPU", to: '/docs/troubleshooting/gpu-not-detected' },
      ],
    },
    {
      slug: 'concepts/local-and-private',
      title: 'Local & private',
      eyebrow: 'Core concepts',
      description: 'Everything runs on your machine — with the exact list of the few times ClipRadar goes online.',
      toc: localAndPrivateToc,
      Body: LocalAndPrivate,
      related: [
        { label: 'Agentic editing (MCP)', to: '/docs/concepts/agentic-editing' },
        { label: 'Where ClipRadar stores things', to: '/docs/reference/where-your-data-lives' },
        { label: 'Report a bug & get help', to: '/docs/get-help' },
      ],
    },
    {
      slug: 'concepts/agentic-editing',
      title: 'Agentic editing (MCP)',
      eyebrow: 'Core concepts',
      description: 'Let an AI agent search your library, verify hits, and build timelines — with you approving each step.',
      toc: agenticEditingToc,
      Body: AgenticEditing,
      related: [
        { label: 'Your first agentic edit', to: '/docs/guides/agentic-editing-quickstart' },
        { label: 'MCP tool reference', to: '/docs/reference/mcp-tools' },
        { label: 'Local & private', to: '/docs/concepts/local-and-private' },
      ],
    },
    {
      slug: 'concepts/licensing',
      title: 'Licensing & activation',
      eyebrow: 'Core concepts',
      description: 'Trial vs paid, moving between machines, offline grace, and what a blocked search really means.',
      toc: licensingToc,
      Body: Licensing,
      related: [
        { label: 'Settings tab', to: '/docs/interface/settings' },
        { label: "I can't search", to: '/docs/troubleshooting/cant-search' },
        { label: 'Frequently asked questions', to: '/docs/faq' },
      ],
    },
  ],
};
